import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { DeviceInclude_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { CallGroupsApi } from '@frontend/api-call-groups';
import { DevicesQueries } from '@frontend/api-devices';
import { SipProfilesApiTypes } from '@frontend/api-sip-profile';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, Table, Text, useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { usePhoneSettingsQueryByLocations } from '../../utils/use-phone-settings-query';
import { PhoneSettingsScopedLocationFilterMenu } from '../common/index-table';

type Props = ModalControlModalProps & {
  callGroupIds: (string | undefined)[];
  deviceName: string | undefined;
  locationIds: string[] | undefined;
  onSave: (
    cg: SipProfilesApiTypes.SipProfileCallGroupType
  ) => Promise<SipProfilesApiTypes.ReplaceCallGroupsType['output']>;
};
export const AssignDeviceToCallGroups = ({ callGroupIds, deviceName, locationIds, onSave, ...rest }: Props) => {
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const [searchValue, setSearchValue] = useState('');
  const [selectedCallGroups, setSelectedCallGroups] = useState<SipProfilesApiTypes.SipProfileCallGroupType>();
  const alerts = useAlert();

  const resolvedLocations = filteredLocations.length > 0 ? filteredLocations : globalAvailableLocationIds;

  const { data: { devices } = { devices: [] } } = DevicesQueries.useGetDevicesList({
    include: [DeviceInclude_Enum.CALL_GROUPS],
    tenantId: settingsTenantLocation?.phoneTenantId,
  });

  const { data: callGroups, isLoading } = usePhoneSettingsQueryByLocations({
    queryKey: queryKeys.callGroups(),
    queryFn: () => CallGroupsApi.listCallGroups(),
    locationIds: settingsTenantLocation?.locationType === 'unify' ? resolvedLocations : undefined,
    select: (data) => {
      return Object.entries(data)
        .map(([locationId, callGroups]) => {
          return callGroups.map((callGroup) => {
            if (callGroupIds?.includes(callGroup?.ID)) return;
            return {
              ...callGroup,
              devices: devices?.filter((device) => {
                if (!device.sipProfiles || !device.sipProfiles[0]?.callGroups?.length) return;
                return device.sipProfiles[0].callGroups?.some((callgroup) => callgroup.id === callGroup.ID);
              }),
              locationId,
            };
          });
        })
        .flat()
        .filter(Boolean)
        .filter((cg) => !cg?.name.toLocaleLowerCase().includes('e911'))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    meta: {
      error: {
        message: 'AssignDeviceToCallGroups:callGroupsQuery | Failed to fetch call groups for device assignment',
        data: { locationIds: resolvedLocations },
      },
    },
  });

  const searchedCallGroups = useMemo(() => {
    const search = searchValue.toLowerCase();

    return callGroups?.filter((callgroup) => callgroup.name.toLowerCase().includes(search));
  }, [callGroups, searchValue]);

  return (
    <Modal {...rest} maxWidth={600}>
      <Modal.Header>{t('Assign to Call Groups')}</Modal.Header>
      <Modal.Body>
        <Text>
          <Trans>
            Assign <strong>{deviceName}</strong> to any relevant call groups so that it will receive incoming calls to
            the office and ring with other devices.
          </Trans>
        </Text>
        <Table
          emptyStateConfig={{
            type: 'sync_your_phone',
            header: t('No Call Groups'),
          }}
          isSelectable
          isPaginated
          rowSelectionConfig={{
            hideBulkSelection: true,
            onSelectionToggle: (_s, data) => {
              setSelectedCallGroups(data.map(({ ID, name }) => ({ id: ID, name })));
            },
          }}
          hasGlobalSearch
          globalSearchConfig={{
            searchHandler: setSearchValue,
            placeholder: t('Search Call Group or Device'),
            position: settingsTenantLocation?.locationType === 'unify' ? 'right' : 'left',
          }}
          colConfig={[
            {
              Header: t('Name'),
              accessor: ({ name }: { name: string }) => name,
              id: 'name',
              sortType: 'caseInsensitive',
            },
            {
              Header: t('Devices'),
              id: 'num-of-devices',
              accessor: ({ devices }) => devices,
              cellRenderer: (devices) => {
                return !!devices.length && <Text>{t('{{count}} devices', { count: devices.length })}</Text>;
              },
              disableSortBy: true,
            },
          ]}
          data={searchedCallGroups || callGroups || []}
          hasResponsiveColWidths
          isLoading={isLoading}
          customToolbarRender={
            settingsTenantLocation?.locationType === 'unify'
              ? () => (
                  <PhoneSettingsScopedLocationFilterMenu
                    selectedOptions={resolvedLocations}
                    onChange={setFilteredLocations}
                  />
                )
              : undefined
          }
          wrapperStyle={css`
            margin-top: ${theme.spacing(4)};

            .table-toolbar {
              justify-content: space-between;
            }
          `}
          expandableRowComponent={(row) => {
            return (
              <div
                css={css`
                  width: 100%;
                  padding: ${theme.spacing(2)} ${theme.spacing(4)};
                  overflow-y: auto;
                  height: 300px;
                  display: flex;
                  flex-direction: column;
                  height: auto;
                `}
              >
                <Text
                  size='medium'
                  color='light'
                  css={css`
                    margin-bottom: ${theme.spacing(2)};
                  `}
                >
                  {!!row.devices?.length ? t('Devices in Call Group') : t('No Devices Assigned Yet')}
                </Text>
                {row.devices?.map((device) => {
                  return (
                    <div key={device.deviceId}>
                      <Text
                        as='div'
                        css={css`
                          display: flex;
                          flex-direction: column;
                        `}
                      >
                        <Text
                          css={css`
                            margin: ${theme.spacing(2)} ${theme.spacing(1)};
                          `}
                        >
                          {device.name}
                        </Text>
                      </Text>
                    </div>
                  );
                })}
              </div>
            );
          }}
        />
      </Modal.Body>
      <Modal.Actions
        onSecondaryClick={rest.onClose}
        secondaryLabel={t('Cancel')}
        primaryLabel={t('Save')}
        disablePrimary={!selectedCallGroups?.length}
        onPrimaryClick={() => {
          if (!!selectedCallGroups?.length)
            onSave(selectedCallGroups).then(() => {
              rest.onClose();
              alerts.success(t('Saved Call Group Assignments'));
            });
        }}
      />
    </Modal>
  );
};
